import React from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import Button from "@/ui/Button/Button";
import cls from "./styles.module.scss";

const getErrorMessage = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unknown error";
};

const getErrorStackTrace = (error: unknown, componentStack = "") => {
  const errorStack =
    error instanceof Error
      ? error.stack
      : isRouteErrorResponse(error)
        ? error.data
        : String(error);

  return [
    getErrorMessage(error),
    errorStack,
    componentStack ? `Component stack:${componentStack}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
};

interface ErrorFallbackProps {
  error: unknown;
  componentStack?: string;
}

export const ErrorFallback = ({
  error,
  componentStack = "",
}: ErrorFallbackProps) => {
  const [copied, setCopied] = React.useState(false);
  const stackTrace = getErrorStackTrace(error, componentStack);

  const copyStackTrace = async () => {
    try {
      await navigator.clipboard.writeText(stackTrace);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <main className={cls.root}>
      <section className={cls.panel}>
        <div className={cls.header}>
          <div>
            <p className={cls.eyebrow}>Что-то пошло не так</p>
            <h1 className={cls.title}>Не удалось открыть страницу</h1>
          </div>
          <Button
            className={cls.copyButton}
            label={copied ? "Скопировано" : "Скопировать детали"}
            variant="graphite"
            onClick={copyStackTrace}
          />
        </div>
        <p className={cls.message}>
          Попробуйте обновить страницу. Если ошибка повторится, отправьте детали
          в поддержку.
        </p>
        <pre className={cls.stackTrace}>
          <code>{stackTrace}</code>
        </pre>
      </section>
    </main>
  );
};

export const RouteErrorBoundary = () => {
  const error = useRouteError();

  return <ErrorFallback error={error} />;
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    error: null,
    componentStack: "",
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      componentStack: errorInfo.componentStack ?? "",
    });
  }

  render() {
    const { error, componentStack } = this.state;

    if (!error) {
      return this.props.children;
    }

    return <ErrorFallback error={error} componentStack={componentStack} />;
  }
}

export default ErrorBoundary;
